import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import AITutorPanel from "@/components/AITutorPanel";
import SimulationCanvas from "@/components/SimulationCanvas";
import Simulation3DCanvas, { HAS_3D } from "@/components/3d/Simulation3DCanvas";
import GuidedFlow from "@/components/stages/GuidedFlow";
import { useFlagshipIds } from "@/hooks/useFlagshipIds";
import { CollabBar } from "@/components/CollabBar";
import { useCollabRoom } from "@/hooks/useCollabRoom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BrimiLogo } from "@/components/OwlMascot";
import { ArrowLeft, Save, RotateCcw, Beaker, ClipboardCheck, Sparkles, Box, Square } from "lucide-react";
import { toast } from "sonner";

const ExperimentWorkspace = () => {
  const { experimentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [exp, setExp] = useState(null);
  const [params, setParams] = useState({});
  const [saving, setSaving] = useState(false);
  const [observations, setObservations] = useState([]);
  const [view3D, setView3D] = useState(false);
  const [guidedMode, setGuidedMode] = useState(true); // default to guided when flagship
  const flagshipIds = useFlagshipIds();

  // Collab room state (URL ?room=CODE)
  const [roomCode, setRoomCode] = useState(searchParams.get("room"));

  const handleRemoteParams = useCallback((remote) => {
    setParams((prev) => ({ ...prev, ...remote }));
  }, []);

  const { connected, participants, myId, chat, sendParams, sendChat } = useCollabRoom(
    roomCode, user?.name || "Guest", handleRemoteParams
  );

  const joinRoom = (code) => {
    setRoomCode(code);
    setSearchParams({ room: code });
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/experiments/${experimentId}`);
      setExp(data);
      const init = {};
      data.params.forEach((p) => { init[p.key] = p.default; });
      setParams(init);
    })();
  }, [experimentId]);

  // Redirect to paywall if trial expired / no active sub (except owner/teacher)
  const { billing } = useAuth();
  useEffect(() => {
    if (billing && !billing.active) navigate("/paywall", { replace: true });
  }, [billing, navigate]);

  const setParam = (key, val) => {
    setParams((p) => {
      const next = { ...p, [key]: val };
      if (connected) sendParams({ [key]: val });
      return next;
    });
  };

  const resetParams = () => {
    if (!exp) return;
    const init = {};
    exp.params.forEach((p) => { init[p.key] = p.default; });
    setParams(init);
    setObservations([]);
    toast("Parameters reset.");
  };

  const recordObservation = () => {
    setObservations((o) => [...o, { ...params, ts: new Date().toISOString() }]);
    toast.success("Observation recorded.");
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/reports", {
        experiment_id: experimentId,
        parameters: params,
        observations,
      });
      toast.success("Lab report generated! +25 XP 🎉");
      navigate(`/report/${data.report_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  if (!exp) {
    return <div className="min-h-screen bg-slate-950 text-slate-500 flex items-center justify-center font-mono">Loading experiment…</div>;
  }

  return (
    <div className="min-h-screen h-screen flex flex-col bg-slate-950 text-slate-100 overflow-hidden">
      {/* Top bar */}
      <header className="shrink-0 h-14 border-b border-slate-800 bg-slate-900/70 backdrop-blur-xl flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-300" data-testid="workspace-back-btn" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <BrimiLogo size={28} />
          <div className="leading-tight">
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold font-mono">Class {exp.grade} · {exp.subject}</div>
            <div className="font-outfit font-bold text-sm text-slate-100" data-testid="workspace-title">{exp.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {flagshipIds.has(exp.id) && (
            <div className="flex items-center gap-1 bg-slate-800/60 border border-cyan-500/30 rounded-full p-1 mr-1">
              <button
                onClick={() => setGuidedMode(true)}
                className={`px-3 h-7 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${guidedMode ? "bg-cyan-500 text-slate-950" : "text-slate-300"}`}
                data-testid="mode-guided-btn"
              >
                Guided
              </button>
              <button
                onClick={() => setGuidedMode(false)}
                className={`px-3 h-7 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${!guidedMode ? "bg-cyan-500 text-slate-950" : "text-slate-300"}`}
                data-testid="mode-sandbox-btn"
              >
                Sandbox
              </button>
            </div>
          )}
          <CollabBar
            experimentId={experimentId}
            connected={connected}
            participants={participants}
            myId={myId}
            chat={chat}
            sendChat={sendChat}
            onJoin={joinRoom}
          />
          <div className="hidden md:block w-px h-6 bg-slate-800 mx-1" />
          <Button variant="ghost" size="sm" onClick={resetParams} className="text-slate-300 hover:bg-slate-800" data-testid="workspace-reset-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button size="sm" onClick={submit} disabled={saving} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-full" data-testid="workspace-submit-btn">
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Generating…" : "Submit & get report"}
          </Button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {guidedMode && flagshipIds.has(exp.id) ? (
          // ===== GUIDED FLOW =====
          <>
            <AITutorPanel experimentId={experimentId} experiment={exp} currentUser={user} />
            <main className="flex-1 overflow-hidden">
              <GuidedFlow experiment={exp} />
            </main>
          </>
        ) : (
          // ===== SANDBOX MODE (original 3-column) =====
          <>
            {/* Left: AI tutor */}
            <AITutorPanel experimentId={experimentId} experiment={exp} currentUser={user} />

        {/* Center: Simulation */}
        <main className="flex-1 relative overflow-hidden grid-lines">
          <div className="glow-blob bg-cyan-500/30 w-96 h-96 -top-24 left-1/3" />
          <div className="glow-blob bg-blue-500/20 w-96 h-96 bottom-0 right-0" />
          <div className="relative h-full flex items-center justify-center p-6">
            {view3D && HAS_3D.has(exp.id) ? (
              <div className="w-full h-full">
                <Simulation3DCanvas experiment={exp} params={params} />
              </div>
            ) : (
              <SimulationCanvas experiment={exp} params={params} />
            )}
          </div>
          <div className="absolute bottom-4 left-4 flex items-center gap-2 text-xs font-mono text-slate-400">
            <Beaker className="h-3.5 w-3.5" /> Virtual lab · Safe mode
          </div>
          {HAS_3D.has(exp.id) && (
            <div className="absolute top-3 left-3 z-30 flex items-center gap-1 bg-slate-900/80 backdrop-blur-md border border-cyan-500/30 rounded-full p-1 shadow-lg">
              <button
                onClick={() => setView3D(false)}
                className={`flex items-center gap-1.5 px-3 h-7 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${!view3D ? "bg-cyan-500 text-slate-950" : "text-slate-300 hover:text-cyan-300"}`}
                data-testid="view-2d-btn"
              >
                <Square className="h-3.5 w-3.5" /> 2D
              </button>
              <button
                onClick={() => setView3D(true)}
                className={`flex items-center gap-1.5 px-3 h-7 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${view3D ? "bg-cyan-500 text-slate-950" : "text-slate-300 hover:text-cyan-300"}`}
                data-testid="view-3d-btn"
              >
                <Box className="h-3.5 w-3.5" /> 3D
              </button>
            </div>
          )}
        </main>

        {/* Right: Controls + Observations + Theory */}
        <aside className="w-96 shrink-0 border-l border-slate-800 bg-slate-900/60 backdrop-blur-xl flex flex-col h-full">
          <Tabs defaultValue="controls" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-3 mx-4 mt-3 bg-slate-800/80 border border-slate-700 rounded-lg h-9">
              <TabsTrigger value="controls" data-testid="tab-controls" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950 text-xs">Controls</TabsTrigger>
              <TabsTrigger value="observations" data-testid="tab-observations" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950 text-xs">Observations</TabsTrigger>
              <TabsTrigger value="theory" data-testid="tab-theory" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950 text-xs">Theory</TabsTrigger>
            </TabsList>

            <TabsContent value="controls" className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
              {exp.params.map((p) => (
                <div key={p.key}>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-slate-300 text-sm">{p.label}</Label>
                    {p.type !== "select" && (
                      <span className="text-cyan-300 font-mono text-sm" data-testid={`param-value-${p.key}`}>
                        {typeof params[p.key] === "number" ? params[p.key] : params[p.key]} {p.unit}
                      </span>
                    )}
                  </div>
                  {p.type === "select" ? (
                    <Select value={params[p.key]} onValueChange={(v) => setParam(p.key, v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100" data-testid={`param-select-${p.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {p.options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Slider
                      min={p.min}
                      max={p.max}
                      step={p.step}
                      value={[+params[p.key]]}
                      onValueChange={(v) => setParam(p.key, v[0])}
                      className="mt-1"
                      data-testid={`param-slider-${p.key}`}
                    />
                  )}
                </div>
              ))}
              <Button
                type="button"
                onClick={recordObservation}
                className="w-full rounded-full bg-slate-800 border border-cyan-500/40 text-cyan-300 hover:bg-slate-700 mt-4"
                variant="ghost"
                data-testid="workspace-record-btn"
              >
                <ClipboardCheck className="mr-2 h-4 w-4" /> Record observation
              </Button>
            </TabsContent>

            <TabsContent value="observations" className="flex-1 overflow-y-auto px-4 py-4">
              {observations.length === 0 ? (
                <div className="text-slate-500 text-sm">
                  <Sparkles className="h-5 w-5 mb-2 text-cyan-400" />
                  Adjust the parameters, then hit <b>Record observation</b> to snapshot the current state.
                </div>
              ) : (
                <div className="space-y-2">
                  {observations.map((o, i) => (
                    <div key={i} className="p-3 rounded-lg bg-slate-800/70 border border-slate-700 text-xs font-mono text-slate-300" data-testid={`obs-row-${i}`}>
                      <div className="text-cyan-400 font-bold mb-1">#{i + 1}</div>
                      {Object.entries(o).filter(([k]) => k !== "ts").map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-slate-400">{k}</span>
                          <span>{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="theory" className="flex-1 overflow-y-auto px-4 py-4 text-sm text-slate-300 leading-relaxed space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-1">Aim</div>
                <div>{exp.aim}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-1">Theory</div>
                <div>{exp.theory}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-1">Apparatus</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {exp.apparatus.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </aside>
          </>
        )}
      </div>
    </div>
  );
};

export default ExperimentWorkspace;
